// KPICard — large stat card for top-line metrics

import type { ReactNode } from "react";

type ColorAccent = "green" | "red" | "amber" | "neutral";

interface KPICardProps {
  /** Stat label. Accepts a node so an <InfoTip/> can sit beside the text. */
  label: ReactNode;
  value: string | number;
  subtitle?: string;
  trend?: {
    direction: "up" | "down";
    /** Formatted string like "+12%" or "−3%" */
    label: string;
    /** Whether "up" is good (true) or "down" is good (false). Affects color. */
    positiveIsGood?: boolean;
  };
  /**
   * Set when the value's source has not refreshed and no trend can honestly be
   * drawn. Renders in the trend slot so the absence of an arrow reads as
   * "withheld", not as "no change" — an empty trend slot is silence, and on a
   * frozen feed silence is indistinguishable from a flat metric. When set,
   * `trend` is IGNORED: a stale source cannot produce a trustworthy delta.
   */
  staleNote?: string;
  accent?: ColorAccent;
  isLoading?: boolean;
}

const ACCENT_CLASSES: Record<ColorAccent, string> = {
  green: "text-status-green",
  red: "text-status-red",
  amber: "text-status-amber",
  neutral: "text-theme-primary",
};

const ACCENT_BAR: Record<ColorAccent, string> = {
  green: "bg-status-green",
  red: "bg-status-red",
  amber: "bg-status-amber",
  neutral: "bg-dcvfd-accent",
};

function TrendArrow({ direction }: { direction: "up" | "down" }) {
  if (direction === "up") {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="18 15 12 9 6 15" />
      </svg>
    );
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export default function KPICard({
  label,
  value,
  subtitle,
  trend,
  staleNote,
  accent = "neutral",
  isLoading = false,
}: KPICardProps) {
  // A stale source outranks any delta we could compute from it.
  const effectiveTrend = staleNote ? undefined : trend;

  // Compute trend color: if positiveIsGood (default true), up=green, down=red
  const positiveIsGood = effectiveTrend?.positiveIsGood ?? true;
  let trendColor = "text-theme-muted";
  if (effectiveTrend) {
    const isPositive =
      effectiveTrend.direction === "up" ? positiveIsGood : !positiveIsGood;
    trendColor = isPositive ? "text-status-green" : "text-status-red";
  }

  return (
    <div className="glass rounded-2xl p-5 flex flex-col gap-3 relative overflow-hidden">
      {/* Accent bar — top edge */}
      <div
        className={`absolute top-0 left-0 right-0 h-0.5 ${ACCENT_BAR[accent]}`}
      />

      {/* Label */}
      <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider leading-none pt-1">
        {label}
      </p>

      {/* Value */}
      {isLoading ? (
        <div className="skeleton h-10 w-32 rounded-md" />
      ) : (
        <p
          className={`text-4xl font-bold leading-none tabular-nums ${ACCENT_CLASSES[accent]}`}
        >
          {typeof value === "number" ? value.toLocaleString() : value}
        </p>
      )}

      {/* Trend + subtitle row */}
      <div className="flex items-center justify-between gap-2 flex-wrap min-h-[1.25rem]">
        {isLoading ? (
          <div className="skeleton h-4 w-40 rounded-md" />
        ) : (
          <>
            {subtitle && (
              <p className="text-xs text-theme-muted leading-snug">
                {subtitle}
              </p>
            )}
            {staleNote ? (
              <span
                role="status"
                data-testid="kpi-stale-note"
                title={staleNote}
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide ml-auto shrink-0 rounded border border-amber-500/30 bg-amber-500/15 px-1.5 py-0.5 text-amber-700 dark:text-amber-400"
              >
                {staleNote}
              </span>
            ) : (
              effectiveTrend && (
                <span
                  className={`inline-flex items-center gap-0.5 text-xs font-semibold ml-auto shrink-0 ${trendColor}`}
                >
                  <TrendArrow direction={effectiveTrend.direction} />
                  {effectiveTrend.label}
                </span>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
